import React from 'react';
import { useNavigate } from 'react-router-dom';
import {
  BarChart3,
  Building2,
  ClipboardList,
  Clock,
  DollarSign,
  FileText,
  List,
  Package,
  Receipt,
  RotateCcw,
  ShoppingCart,
  TrendingUp,
  Truck,
  UserCheck,
  UserCog,
  Users,
  ChevronRight,
} from 'lucide-react';
import { getReportSectionById } from './reportRouteMeta';

const ICON_BY_NAME = {
  ShoppingCart,
  ClipboardList,
  Truck,
  Package,
  Building2,
  Users,
  UserCheck,
  UserCog,
  Receipt,
  DollarSign,
  RotateCcw,
  TrendingUp,
};

/** Small icon per report slug for card thumbnails. */
const CARD_ICON_BY_SLUG = {
  'order-report': FileText,
  'pending-orders': Clock,
  'advance-orders': List,
  'delivery-report': Truck,
  'shipment-report': Package,
  'shipment-expense-report': Receipt,
  'inactive-clients': Users,
  'client-orders': Building2,
  'customer-orders': Users,
  'driver-commission': DollarSign,
  'employee-orders': UserCheck,
  'driver-expense': Receipt,
  'agent-orders': UserCog,
  'company-expense': Building2,
  'client-payment-report': DollarSign,
  'client-return-report': RotateCcw,
  'income-expense': TrendingUp,
  'vat-report': BarChart3,
};

function resolveSectionIcon(name) {
  return ICON_BY_NAME[name] || FileText;
}

function resolveCardIcon(slug) {
  return CARD_ICON_BY_SLUG[slug] || FileText;
}

export default function ReportCategoryHub({ sectionId }) {
  const navigate = useNavigate();
  const section = getReportSectionById(sectionId);
  const SectionIcon = section ? resolveSectionIcon(section.iconName) : FileText;

  if (!section) {
    return (
      <div className="rounded-lg border border-amber-200 bg-amber-50 px-4 py-8 text-center text-amber-900 text-sm">
        Unknown reports section.
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-6xl space-y-8">
      <div className="relative overflow-hidden rounded-2xl border border-emerald-100/80 bg-gradient-to-br from-emerald-50/90 via-white to-white px-6 py-7 shadow-sm sm:px-8 sm:py-8">
        <div
          className="pointer-events-none absolute -right-16 -top-16 h-48 w-48 rounded-full bg-emerald-200/25 blur-2xl"
          aria-hidden
        />
        <div className="relative flex flex-wrap items-center gap-4 sm:gap-5">
          <span className="flex h-14 w-14 shrink-0 items-center justify-center rounded-2xl bg-white text-emerald-700 shadow-md ring-1 ring-emerald-100">
            <SectionIcon className="h-7 w-7" aria-hidden />
          </span>
          <div className="min-w-0 flex-1">
            <h2 className="page-title text-2xl font-bold tracking-tight text-gray-900 sm:text-3xl">
              {section.navLabel}
            </h2>
          </div>
        </div>
      </div>

      {section.cards.length === 0 ? (
        <div className="rounded-2xl border border-dashed border-gray-300 bg-gray-50/80 px-8 py-16 text-center">
          <ClipboardList className="mx-auto h-10 w-10 text-gray-400" aria-hidden />
          <p className="mt-3 text-sm font-medium text-gray-700">No reports in this category yet</p>
          <p className="mt-1 text-xs text-gray-500">Check back later or use another reports section.</p>
        </div>
      ) : (
        <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-3">
          {section.cards.map((card) => {
            const to = `/home/reports/${card.slug}`;
            const CardIcon = resolveCardIcon(card.slug);
            return (
              <button
                key={card.slug}
                type="button"
                onClick={() => navigate(to)}
                aria-label={`Open ${card.label}`}
                className="group relative flex min-h-[5.5rem] w-full items-center gap-4 overflow-hidden rounded-2xl border border-gray-200/90 bg-white p-5 text-left shadow-sm ring-0 transition duration-200 hover:-translate-y-0.5 hover:border-emerald-200 hover:shadow-lg focus:outline-none focus-visible:ring-2 focus-visible:ring-emerald-500 focus-visible:ring-offset-2 active:translate-y-0 active:scale-[0.99]"
              >
                <span
                  className="absolute left-0 top-0 h-full w-1 rounded-l-2xl bg-emerald-500 opacity-0 transition-opacity duration-200 group-hover:opacity-100"
                  aria-hidden
                />
                <span className="flex h-12 w-12 shrink-0 items-center justify-center rounded-xl bg-emerald-50 text-emerald-700 ring-1 ring-emerald-100/80 transition group-hover:bg-emerald-100 group-hover:ring-emerald-200/80">
                  <CardIcon className="h-6 w-6" aria-hidden />
                </span>
                <span className="min-w-0 flex-1 text-base font-semibold leading-snug text-gray-900 transition group-hover:text-emerald-900">
                  {card.label}
                </span>
                <ChevronRight
                  className="h-5 w-5 shrink-0 text-gray-300 transition group-hover:translate-x-0.5 group-hover:text-emerald-600"
                  aria-hidden
                />
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}
